"use client";

import { useState, type FormEvent } from "react";
import {
  Eye,
  EyeOff,
  Lock,
  Mail,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/features/auth/hooks/use-auth";

const featureItems = [
  {
    icon: "/icons/gps-location.png",
    title: "Định vị GPS",
    description: "Theo dõi vị trí thời gian thực",
  },
  {
    icon: "/icons/camera-monitoring.png",
    title: "Giám sát camera",
    description: "Ghi hình & giám sát trực tuyến",
  },
  {
    icon: "/icons/transparent-evaluation.png",
    title: "Đánh giá minh bạch",
    description: "Chấm điểm tự động, kết quả chính xác",
  },
  {
    icon: "/icons/centralized-management.png",
    title: "Quản lý tập trung",
    description: "Dữ liệu đồng bộ, báo cáo trực quan",
  },
];

const stats = [
  {
    value: "100+",
    label: "Trung tâm sát hạch",
  },
  {
    value: "1000+",
    label: "Xe sát hạch",
  },
  {
    value: "100.000+",
    label: "Phiên sát hạch",
  },
  {
    value: "99.9%",
    label: "Độ chính xác",
  },
];

export default function Home() {
  const router = useRouter();
  const { login } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    console.log("[LOGIN] Calling login API", { username });
    setLoginError(null);

    try {
      await login({
        email: username,
        password,
      });

      console.log("[LOGIN] Login success", { username });
      router.push("/monitoring");
    } catch (error) {
      console.error("[LOGIN] Login failed", error);
      setLoginError("Đăng nhập thất bại. Vui lòng kiểm tra tài khoản hoặc mật khẩu.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#eef2f5] p-2.5 sm:p-3 lg:p-3">
      <div className="grid min-h-[calc(100vh-24px)] overflow-hidden rounded-[24px] bg-white shadow-[0_8px_40px_rgba(15,23,42,0.08)] lg:grid-cols-[70%_30%]">
        {/* =========================================================
            LEFT - HERO
        ========================================================== */}
        <section className="relative min-h-[760px] overflow-hidden lg:min-h-[calc(100vh-24px)]">
          {/* Background */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('/icons/login1.png')",
            }}
          />

          {/* Very light overlay - KHÔNG làm ảnh bị trắng quá */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/45 via-white/15 to-transparent" />

          {/* Bottom subtle overlay */}
          <div className="absolute inset-x-0 bottom-0 h-[42%] bg-gradient-to-t from-white/10 via-transparent to-transparent" />

          {/* Hero content */}
          <div className="relative z-10 flex min-h-full flex-col px-8 pb-7 pt-8 sm:px-10 lg:px-[50px] lg:pb-8 lg:pt-10">
            {/* =====================================================
                LOGO
            ====================================================== */}
            <div className="flex items-center gap-3">
              <img
                src="/icons/logo.png"
                alt="EcoTek"
                className="h-[38px] w-[148px] object-contain"
              />
            </div>

            {/* =====================================================
                HERO TEXT
            ====================================================== */}

        <div className="mt-[25px] max-w-[700px]">
          {/* Label */}
          <p className="text-[15px] font-semibold uppercase tracking-[0.28em] text-[#008f94]">
            HỆ THỐNG
          </p>

          {/* Main title */}
          <h1 className="mt-3 max-w-[680px] text-[42px] font-bold leading-[1.1] tracking-[-0.025em] text-[#071a36] sm:text-[44px] lg:text-[46px]">
            GIÁM SÁT XE &amp; CAMERA
            <br />
            SÁT HẠCH LÁI XE
          </h1>

          {/* Description */}
          <p className="mt-4 max-w-[590px] text-[15px] font-normal leading-[1.6] text-[#344b65]">
            Giải pháp giám sát toàn diện, hỗ trợ đánh giá chính xác
            <br className="hidden sm:block" />
            và nâng cao chất lượng công tác sát hạch lái xe.
          </p>
        </div>


        {/* =====================================================
            FEATURES
        ====================================================== */}
        <div className="mt-[28px] max-w-[650px]">
          <div className="grid grid-cols-4 gap-0">
            {featureItems.map((item, index) => (
              <div
                key={item.title}
                className={[
                  "relative flex w-[145px] min-w-0 flex-col items-center text-center px-0",
                  index > 0
                    ? "lg:border-l lg:border-[#7aa6b5]/40"
                    : "",
                ].join(" ")}
              >
                {/* ICON */}
                <div className="flex h-[44px] w-[44px] items-center justify-center">
                  <img
                    src={item.icon}
                    alt={item.title}
                    className="h-[38px] w-[38px] object-contain"
                  />
                </div>

                {/* TITLE */}
                <h3 className="mt-2 min-h-[32px] text-[13px] font-bold leading-[1.25] text-[#0c203d]">
                  {item.title}
                </h3>

                {/* DESCRIPTION */}
                <p className="mt-0.5 max-w-[115px] text-[11px] leading-[1.4] text-[#334b68]">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>

            {/* =====================================================
                STATS
            ====================================================== */}
            <div className="mt-auto pt-8">
              <div className="max-w-[730px] overflow-hidden rounded-[18px] bg-[#07152f]/95 px-3 py-4 shadow-[0_15px_40px_rgba(7,21,47,0.18)] backdrop-blur-sm sm:px-4 sm:py-4">
                <div className="grid grid-cols-2 lg:grid-cols-4">
                  {stats.map((item, index) => (
                    <StatItem
                      key={item.label}
                      value={item.value}
                      label={item.label}
                      divider={index > 0}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================
            RIGHT - LOGIN AREA
        ========================================================== */}
        <section className="relative flex min-h-[760px] items-center justify-start bg-[#f4f7f9] p-4 sm:p-5 lg:min-h-[calc(100vh-24px)] lg:pl-4 lg:pr-6">
          {/* LOGIN CARD */}
          <div className="flex min-h-[calc(100vh-72px)] w-full max-w-[550px] flex-col rounded-[20px] bg-white px-7 py-10 shadow-[0_4px_24px_rgba(15,23,42,0.045)] sm:px-10 sm:py-12 lg:px-[56px] lg:py-12">
            {/* =====================================================
                LOGIN HEADER
            ====================================================== */}
            <div className="text-center">
              <div className="mx-auto flex h-[78px] w-[78px] items-center justify-center">
                <img
                  src="/icons/login_logo.png"
                  alt="EcoTek"
                  className="h-[74px] w-[74px] object-contain"
                />
              </div>

              <h2 className="mt-5 text-[29px] font-bold tracking-[-0.025em] text-[#071a36]">
                Đăng nhập hệ thống
              </h2>

              <p className="mt-2 text-[15px] text-[#64748b]">
                Vui lòng nhập thông tin để tiếp tục
              </p>
            </div>

            {/* =====================================================
                FORM
            ====================================================== */}
            <form
              className="mt-8 flex flex-1 flex-col"
              onSubmit={handleSubmit}
            >
              <div className="space-y-5">
                {/* Username */}
                <div>
                  <label
                    htmlFor="username"
                    className="mb-2.5 block text-[14px] font-semibold text-[#172b4d]"
                  >
                    Tên đăng nhập
                  </label>

                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 h-[19px] w-[19px] -translate-y-1/2 text-[#718198]" />

                    <input
                      id="username"
                      name="username"
                      value={username}
                      onChange={(event) =>
                        setUsername(event.target.value)
                      }
                      type="text"
                      autoComplete="username"
                      placeholder="Nhập tên đăng nhập"
                      className="h-[55px] w-full rounded-[9px] border border-[#d5dde7] bg-white pl-[48px] pr-4 text-[15px] text-[#172b4d] outline-none transition placeholder:text-[#8b98aa] hover:border-[#b9c6d5] focus:border-[#008f94] focus:ring-[3px] focus:ring-[#008f94]/10"
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label
                    htmlFor="password"
                    className="mb-2.5 block text-[14px] font-semibold text-[#172b4d]"
                  >
                    Mật khẩu
                  </label>

                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-4 top-1/2 h-[19px] w-[19px] -translate-y-1/2 text-[#718198]" />

                    <input
                      id="password"
                      name="password"
                      value={password}
                      onChange={(event) =>
                        setPassword(event.target.value)
                      }
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="Nhập mật khẩu"
                      className="h-[55px] w-full rounded-[9px] border border-[#d5dde7] bg-white pl-[48px] pr-[48px] text-[15px] text-[#172b4d] outline-none transition placeholder:text-[#8b98aa] hover:border-[#b9c6d5] focus:border-[#008f94] focus:ring-[3px] focus:ring-[#008f94]/10"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowPassword((value) => !value)
                      }
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#718198] transition hover:text-[#008f94]"
                      aria-label={
                        showPassword
                          ? "Ẩn mật khẩu"
                          : "Hiện mật khẩu"
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="h-[19px] w-[19px]" />
                      ) : (
                        <Eye className="h-[19px] w-[19px]" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember / Forgot */}
                <div className="flex items-center justify-between gap-4 pt-0.5 text-[14px]">
                  <label className="inline-flex cursor-pointer items-center gap-2 text-[#4d5d73]">
                    <input
                      type="checkbox"
                      className="h-[18px] w-[18px] rounded border-[#b7c2d0] text-[#008f94] focus:ring-[#008f94]"
                    />
                    <span>Ghi nhớ đăng nhập</span>
                  </label>

                  <button
                    type="button"
                    className="shrink-0 font-medium text-[#008f94] transition hover:text-[#00747a]"
                  >
                    Quên mật khẩu?
                  </button>
                </div>

                {/* Login button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex h-[55px] w-full items-center justify-center rounded-[9px] bg-[#008f94] text-[16px] font-semibold text-white shadow-[0_8px_18px_rgba(0,143,148,0.18)] transition hover:bg-[#007f84] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSubmitting
                    ? "Đang đăng nhập..."
                    : "Đăng nhập"}
                </button>

                {loginError ? (
                  <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {loginError}
                  </div>
                ) : null}

              </div>

              {/* Footer */}
              <div className="mt-auto pt-10 text-center">
                <p className="text-[13px] text-[#8995a6]">
                  © 2026 EcoTek. All rights reserved.
                </p>
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}

/* ===============================================================
   STAT ITEM
=============================================================== */

function StatItem({
  value,
  label,
  divider = false,
}: {
  value: string;
  label: string;
  divider?: boolean;
}) {
  return (
    <div
      className={[
        "px-3 py-1.5 sm:px-4",
        divider ? "lg:border-l lg:border-white/15" : "",
      ].join(" ")}
    >
      <p className="text-[21px] font-bold leading-none tracking-[-0.02em] text-white">
        {value}
      </p>

      <p className="mt-2 text-[12px] leading-4 text-white/65">
        {label}
      </p>
    </div>
  );
}